/**
 * Product description tabs.
 *
 * Desktop: one panel at a time, arrow keys move between tabs.
 * Mobile: an accordion, each panel opens and closes on its own.
 * Without JS both panels stay open, so the content is never hidden.
 */
class ZAProductTabs extends HTMLElement {
  connectedCallback() {
    this.highlightCurrentModel();

    this.tabs = Array.from(this.querySelectorAll('.za-tabs__tab'));
    if (this.tabs.length === 0) return;

    this.panels = this.tabs.map((tab) => this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`));

    this.desktop = window.matchMedia('(min-width: 750px)');
    this.onBreakpointChange = () => this.applyMode();
    this.desktop.addEventListener('change', this.onBreakpointChange);

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.toggle(index));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });

    this.applyMode();
  }

  disconnectedCallback() {
    this.desktop?.removeEventListener('change', this.onBreakpointChange);
  }

  /** Keeps one panel open on desktop, collapses all but the first on mobile. */
  applyMode() {
    const open = this.tabs.findIndex((tab) => tab.getAttribute('aria-expanded') === 'true');
    this.setOpen(open === -1 ? 0 : open, true);
  }

  toggle(index) {
    const isOpen = this.tabs[index].getAttribute('aria-expanded') === 'true';

    if (this.desktop.matches) {
      this.setOpen(index, true);
      return;
    }

    this.tabs[index].setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    this.panels[index].hidden = isOpen;
  }

  setOpen(index, exclusive) {
    this.tabs.forEach((tab, i) => {
      const open = exclusive ? i === index : tab.getAttribute('aria-expanded') === 'true';
      tab.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (this.panels[i]) this.panels[i].hidden = !open;
    });
  }

  onKeydown(event, index) {
    if (!this.desktop.matches) return;

    const step = { ArrowRight: 1, ArrowLeft: -1, Home: -index, End: this.tabs.length - 1 - index }[event.key];
    if (step === undefined) return;

    event.preventDefault();
    const next = (index + step + this.tabs.length) % this.tabs.length;
    this.setOpen(next, true);
    this.tabs[next].focus();
  }

  /**
   * Marks the row that matches this product, so a shared spec table reads as
   * "this model" at a glance. Falls back to no highlight when nothing matches.
   */
  highlightCurrentModel() {
    const model = this.normalize(this.dataset.model);
    if (!model) return;

    this.querySelectorAll('table tr').forEach((row) => {
      const first = row.querySelector('td, th');
      if (!first) return;
      if (this.normalize(first.textContent) !== model) return;

      row.classList.add('za-tabs__row--current');
      first.insertAdjacentHTML('beforeend', '<span class="visually-hidden"> (this model)</span>');
    });
  }

  normalize(value) {
    return (value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }
}

if (!customElements.get('za-product-tabs')) {
  customElements.define('za-product-tabs', ZAProductTabs);
}
